import { useEffect, useState, useRef } from "react";
import useFeedbackStore, { type ToastData } from "@/shared/model/feedbackStore";
import styles from "./Toast.module.scss";

const TOAST_DURATION = 2000;
const EXIT_ANIMATION_DURATION = 240;

const Toast = () => {
  const toast = useFeedbackStore((s) => s.toast);
  const hideToast = useFeedbackStore((s) => s.hideToast);
  const [data, setData] = useState<ToastData | null>(null);
  const [isExiting, setIsExiting] = useState(false);
  const [enterKey, setEnterKey] = useState(0);
  // 토스트가 화면에 표시 중인지(등장 시작 ~ 퇴장 시작 전).
  // 연타 시 이 값이 true 면 재등장 애니 없이 내용·타이머만 갱신한다.
  const stableRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // 외부에서 hideToast() 로 토스트를 비우면 즉시 정리(화면 고착 방지).
    if (!toast) {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      stableRef.current = false;
      setData(null);
      setIsExiting(false);
      return;
    }

    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);

    setData(toast);
    setIsExiting(false);
    // 이미 떠 있는 중이면 재등장 애니를 생략(연타 = 유지 시간만 리셋).
    // 처음 등장하거나 퇴장 중이면 등장 애니를 재생.
    if (!stableRef.current) {
      setEnterKey((k) => k + 1);
    }
    stableRef.current = true;

    hideTimerRef.current = setTimeout(() => {
      stableRef.current = false;
      setIsExiting(true);
      exitTimerRef.current = setTimeout(() => {
        setData(null);
        setIsExiting(false);
        hideToast();
      }, EXIT_ANIMATION_DURATION);
    }, TOAST_DURATION);

    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [toast, hideToast]);

  if (!data) return null;

  return (
    <div
      key={enterKey}
      className={`${styles.toast} ${isExiting ? styles.exit : ""}`}
    >
      <div className={styles.content}>
        {data.icon && (
          <img
            src={data.icon}
            alt=""
            aria-hidden="true"
            className={styles.icon}
          />
        )}
        <span>{data.message}</span>
      </div>
    </div>
  );
};

export default Toast;
